import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Chip, Divider, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import moment from 'moment/moment';

import useNotification from '../../../../../../core/hooks/use_notification';
import { Button, SubmitButton } from '../../../../../common/button';
import { SelectFieldFormik } from '../../../../../common/select_field';
import { MISC_DATE_ISO_FORMAT, MISC_DATE_POLISH_FORMAT } from '../../../../../../core/constants';
import { getOrderStatus, getOrderStatusColor } from '../../../../../common/utils/order_utils';
import { updatePodOrderStatusNotificationData } from '../../../../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../../../../core/enums/common/api';
import { updatePodOrderStatusAction, updatePodOrderStatusState } from '../../../../../../features/order/update_pod_order_status';
import { OrderStatusEnum } from '../../../../../../core/enums/order/order';

const ChangeStatusDialog = ({ order, refreshList }) => {
    const dispatch = useDispatch();
    const { showNotification } = useNotification();

    const updatePodOrderStatus = useSelector(updatePodOrderStatusState);

    const [open, setOpen] = useState(false);

    const initialValues = {
        status: ''
    };

    const schema = Yup.object().shape({
        status: Yup.string().required()
    });

    const onClose = () => setOpen(false);

    const onSubmit = (values) => {
        dispatch(updatePodOrderStatusAction({ id: order?.id, status: values?.status }))
            .then(response => {
                showNotification(updatePodOrderStatusNotificationData(response));
                if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                    refreshList();
                    onClose();
                }
            });
    };

    return (
        <>
            <Tooltip title="Zmień status">
                <IconButton size="small" onClick={ () => setOpen(true) }>
                    <SettingsIcon fontSize="small"/>
                </IconButton>
            </Tooltip>

            <Dialog open={ open } onClose={ onClose } fullWidth maxWidth="sm">
                <Formik
                    initialValues={ initialValues }
                    validationSchema={ schema }
                    validateOnChange={ false }
                    validateOnBlur={ false }
                    onSubmit={ onSubmit }>
                    { () =>
                        <Box component={ Form }>
                            <DialogTitle>
                                <Grid container alignItems="center" justifyContent="space-between">
                                    <Grid>
                                        <Typography variant="h6" fontWeight="bold">
                                            Zakupy { order?.id }
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Data: <b>{ moment(order?.createdDate, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }</b>
                                        </Typography>
                                    </Grid>
                                    <Chip
                                        size="medium"
                                        label={ getOrderStatus(order?.statusName) }
                                        color={ getOrderStatusColor(order?.statusName) }
                                        sx={ { fontWeight: 600 } }
                                    />
                                </Grid>
                            </DialogTitle>
                            <Divider/>

                            <DialogContent sx={ { paddingTop: 3, paddingBottom: 1 } }>
                                <SelectFieldFormik
                                    name="status"
                                    label="Nowy status"
                                    size="small"
                                    options={ [
                                        {
                                            value: OrderStatusEnum.CANCELED.value,
                                            label: OrderStatusEnum.CANCELED.label
                                        },
                                        { value: OrderStatusEnum.FINISHED.value, label: OrderStatusEnum.FINISHED.label }
                                    ] }
                                    required
                                />
                            </DialogContent>

                            <DialogActions sx={ { p: 2, borderTop: theme => `1px solid ${ theme.palette.divider }`, mt: 2 } }>
                                <Button onClick={ onClose } variant="outlined">Zamknij</Button>
                                <SubmitButton isLoading={ updatePodOrderStatus?.loading }>
                                    Zapisz status
                                </SubmitButton>
                            </DialogActions>
                        </Box>
                    }
                </Formik>
            </Dialog>
        </>
    );
};

export default ChangeStatusDialog;